import React, {useEffect} from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import {formStyles} from '../../util/styles';
import Grid from '@mui/material/Grid';
import { createJuror, updateJuror } from '../../db/actions';
import {useDispatch, useSelector, shallowEqual} from 'react-redux';
import {ActionCreators} from '../../state/actions';
import RichTextEditor from '../../components/RichTextEditor';
import { useCase, useJuror } from '../../db/hooks';
import { getDatabase } from '../../db';
import DemographicsOptions from '../../components/Demographics/DemographicsOptions';
import Display from '../../components/RichTextEditor/Display';
import IconButton from '@mui/material/IconButton';
import {Visibility, VisibilityOff, Undo} from '@mui/icons-material';
import {Tooltip} from '@mui/material';
import {useStyles as useRteStyles} from '../../components/RichTextEditor/styles';
import {getMode} from "../../state/ethereal/selectors";
import EmojiIndex from "./EmojiIndex";
import {isMacOs, isWindows} from "react-device-detect";

const AUTO_SAVE_MS = 800;
const UNDO_GROUP_MS = 1200;
const MAX_UNDO_STACK = 40;
import { useCaseCollaboration } from '../../collaboration/CaseCollaborationContext';
import CollaboratorInitials from '../../collaboration/CollaboratorInitials';
import LiveFieldOverlay from '../../collaboration/LiveFieldOverlay';

const useStyles = makeStyles((theme) => ({
    dialog: {
        width: 600,
        position: 'relative',
        overflow: 'visible'
    },
    emojiIndex: {
        backgroundColor: "white",
        padding: "3px 5px 2px 5px",
        borderRadius: "4px",
        fontSize: 14,
        position: "absolute",
        zIndex: 100,
        bottom: 5,
        right: 15,
        boxShadow: "2px 2px 5px -2px black"
    },
    formControl: {
        marginRight: theme.spacing(1),
        minWidth: 200,
    },
    commentsLabel: {
        color: '#808080',
        marginLeft: '5px',
        userSelect: 'none',
        fontSize: '12px',
    },
    textFieldControl: {
        margin: 0,
        minWidth: 500,
    },
    previewButton: {
        marginLeft: 'auto'
    },
    seatNumber: {
        marginLeft: 'auto',
        fontSize: '1rem',
        whiteSpace: 'nowrap'
    },
    dialogTitleContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    flex: {
        display: 'flex',
        alignItems: 'center'
    },
    submitInstructions: {
        marginRight: 'auto',
        color: 'gray',
        fontSize: 14,
        display: "flex",
        flexDirection: "column",
        gap: "0.5em"
    },
    code: {
        backgroundColor: "#333",
        color: "white",
        borderRadius: "4px",
        padding: "3px 5px"
    }
}));

const JurorDialog = ({
                         caseId,
                     }) => {

    const editorRef = React.useRef(null);
    const savingRef = React.useRef(false);
    const activeFieldRef = React.useRef(null);
    // Last server-confirmed comments (text/styles/revision). This is the
    // shared base for the three-way fold-in of remote saves; it only ever
    // advances from the savedJuror live query, never from local edits.
    const lastServerCommentsRef = React.useRef(null);
    const dirtyFieldsRef = React.useRef({});
    const autoSaveTimerRef = React.useRef(null);
    const undoStackRef = React.useRef([]);
    const undoGroupingRef = React.useRef(false);
    const undoGroupTimerRef = React.useRef(null);
    const createdJurorIdRef = React.useRef(null);
    const localCommentsTextRef = React.useRef('');
    // Last payload written to Dexie — compared on close/auto-save so live
    // editor typing is not mistaken for "already saved".
    const lastPersistedRef = React.useRef({
        nameAndId: '',
        comments: '',
        demographics: [],
    });
    const persistChangesRef = React.useRef(null);

    const classes = useStyles();
    const rteClasses = useRteStyles();
    const formClasses = formStyles();

    const dispatch = useDispatch();
    const {jurorInfo, seatNumber, previewingJuror, focusOn, focusAfter} = useSelector((state) => {
        return {
            jurorInfo: state.app.editingJuror,
            seatNumber: state.app.editingSeatNumber,
            previewingJuror: state.app.previewingJuror,
            focusOn: state.app.focusOnEmoji,
            focusAfter: state.app.focusOnOccurrence
        };
    }, shallowEqual);

    const [selectedEmoji, setSelectedEmoji] = React.useState(null);

    const mode = useSelector(getMode);

    const { caseData: caseInfo } = useCase(caseId);
    const { juror: savedJuror } = useJuror(jurorInfo?._id);
    const {
        announceEditing,
        publishLiveState,
        getLiveStatesForJuror,
    } = useCaseCollaboration();

    const [demographicsOptions, setDemographicsOptions] = React.useState([]);

    const [nameAndId, setNameAndId] = React.useState('');
    const [revision, setRevision] = React.useState(0);
    const [demographics, setDemographics] = React.useState([]);
    const [comments, setComments] = React.useState({
        text: '',
        styles: [],
    });
    const [dirtyFields, setDirtyFields] = React.useState({});
    const [canUndo, setCanUndo] = React.useState(false);
    const [saveStatus, setSaveStatus] = React.useState('idle');

    React.useEffect(() => {
        dirtyFieldsRef.current = dirtyFields;
    }, [dirtyFields]);

    const readEditorSnapshot = React.useCallback(() => {
        const content = editorRef.current?.getContent();
        return {
            comments: content?.comments ?? '',
            commentsStyles: content?.richTextStyles ?? [],
            plainText: editorRef.current?.getPlainText?.() ?? '',
        };
    }, []);

    const hasUnpersistedChanges = React.useCallback(() => {
        const editor = readEditorSnapshot();
        const persisted = lastPersistedRef.current;
        return (
            nameAndId !== persisted.nameAndId ||
            editor.comments !== persisted.comments ||
            JSON.stringify(demographics) !==
                JSON.stringify(persisted.demographics)
        );
    }, [demographics, nameAndId, readEditorSnapshot]);

    const markDirty = React.useCallback((field) => {
        if (dirtyFieldsRef.current[field]) {
            return;
        }
        dirtyFieldsRef.current = { ...dirtyFieldsRef.current, [field]: true };
        setDirtyFields(dirtyFieldsRef.current);
    }, []);

    const captureUndoBeforeChange = React.useCallback(() => {
        if (undoGroupingRef.current || previewingJuror) {
            return;
        }

        const content = editorRef.current?.getContent();
        undoStackRef.current.push({
            nameAndId,
            demographics: [...demographics],
            comments: {
                text: content?.comments ?? comments.text,
                styles: [...(content?.richTextStyles ?? comments.styles)],
            },
        });

        if (undoStackRef.current.length > MAX_UNDO_STACK) {
            undoStackRef.current.shift();
        }

        setCanUndo(true);
        undoGroupingRef.current = true;
        clearTimeout(undoGroupTimerRef.current);
        undoGroupTimerRef.current = setTimeout(() => {
            undoGroupingRef.current = false;
        }, UNDO_GROUP_MS);
    }, [nameAndId, demographics, comments, previewingJuror]);

    const jurorId = jurorInfo?._id || null;

    useEffect(() => {
        if (!jurorId) {
            lastServerCommentsRef.current = null;
            createdJurorIdRef.current = null;
            dirtyFieldsRef.current = {};
            lastPersistedRef.current = {
                nameAndId: '',
                comments: '',
                demographics: [],
            };
            setDirtyFields({});
            return;
        }

        if (createdJurorIdRef.current === jurorId) {
            // The id arrived from our own auto-save creating this juror.
            // Keep the in-progress editing state untouched.
            return;
        }

        const source = savedJuror || jurorInfo;
        const text = source?.comments || '';
        const styles = source?.commentsStyles || [];
        lastServerCommentsRef.current = {
            revision: source?.revision || 0,
            comments: text,
            styles,
        };
        localCommentsTextRef.current = text;
        lastPersistedRef.current = {
            nameAndId: source?.nameAndId || '',
            comments: text,
            demographics: source?.demographics ? [...source.demographics] : [],
        };
        // The editor stays mounted across juror switches, so load the new
        // juror's content imperatively when it differs.
        if (editorRef.current?.restoreContent) {
            const editorText = editorRef.current.getPlainText?.() ?? '';
            if (editorText !== text) {
                editorRef.current.restoreContent(text, styles);
            }
        }
        setDirtyFields({});
        dirtyFieldsRef.current = {};
        undoStackRef.current = [];
        setCanUndo(false);
        setSaveStatus('idle');
    }, [jurorId]);

    React.useEffect(() => () => {
        clearTimeout(autoSaveTimerRef.current);
        clearTimeout(undoGroupTimerRef.current);
    }, []);

    useEffect(() => {
        if (focusOn) {
            setSelectedEmoji({
                emoji: focusOn,
                occurrence: focusAfter
            });
        }
        else {
            setSelectedEmoji(null);
        }
    }, [focusOn, focusAfter]);

    useEffect(() => {
        if (jurorId) {
            announceEditing(jurorId);
        } else {
            announceEditing(null);
        }

        return () => {
            announceEditing(null);
        };
    }, [jurorId, announceEditing]);

    useEffect(() => {
        if (!savedJuror || !jurorId) {
            return;
        }

        const savedRev = savedJuror.revision || 0;
        const savedComments = savedJuror.comments || '';
        const savedStyles = savedJuror.commentsStyles || [];

        if (previewingJuror) {
            setRevision(savedRev);
            setNameAndId(savedJuror.nameAndId || '');
            setDemographics(savedJuror.demographics ? [...savedJuror.demographics] : []);
            setComments({ text: savedComments, styles: savedStyles });
            localCommentsTextRef.current = savedComments;
            lastServerCommentsRef.current = {
                revision: savedRev,
                comments: savedComments,
                styles: savedStyles,
            };
            return;
        }

        const base = lastServerCommentsRef.current;
        if (!base) {
            lastServerCommentsRef.current = {
                revision: savedRev,
                comments: savedComments,
                styles: savedStyles,
            };
            return;
        }

        const commentsChanged =
            savedComments !== base.comments ||
            JSON.stringify(savedStyles) !== JSON.stringify(base.styles);
        const revisionAdvanced = savedRev > base.revision;

        if (!revisionAdvanced && !commentsChanged) {
            return;
        }

        setRevision(savedRev);

        if (
            activeFieldRef.current !== 'nameAndId' &&
            !dirtyFieldsRef.current.nameAndId
        ) {
            setNameAndId(savedJuror.nameAndId || '');
        }

        if (savedJuror.demographics && !dirtyFieldsRef.current.demographics) {
            setDemographics([...savedJuror.demographics]);
        }

        if (commentsChanged) {
            if (editorRef.current?.applyRemoteContent) {
                // Three-way fold-in: locally-owned block instances are reused
                // so the user's cursor never moves, even while actively typing.
                // Skipping this when locally editing would cause remote edits
                // to different lines to be silently overwritten on next save.
                const mergedText = editorRef.current.applyRemoteContent(
                    base.comments,
                    savedComments,
                    savedStyles,
                );
                localCommentsTextRef.current = mergedText;
                setComments({ text: mergedText, styles: savedStyles });
                if (mergedText !== savedComments) {
                    console.log(
                        'Folded remote comments into active editor, keeping local edits:',
                        jurorId,
                    );
                }
            } else {
                setComments({ text: savedComments, styles: savedStyles });
                localCommentsTextRef.current = savedComments;
            }
        }

        lastServerCommentsRef.current = {
            revision: savedRev,
            comments: savedComments,
            styles: savedStyles,
        };
    }, [savedJuror, previewingJuror, jurorId]);

    const handleClosePreview = (event) => {
        return handleClose(event, 'closeClick');
    };

    const persistChanges = React.useCallback(async () => {
        if (savingRef.current || previewingJuror || !jurorInfo) {
            return;
        }

        const effectiveJurorId =
            jurorInfo._id || createdJurorIdRef.current;
        const isEdit = !!effectiveJurorId;

        // New juror: only create if there's actual content to save
        if (!isEdit && !hasUnpersistedChanges()) {
            return;
        }

        // Existing juror: skip if nothing has changed
        if (
            isEdit &&
            Object.keys(dirtyFieldsRef.current).length === 0 &&
            !hasUnpersistedChanges()
        ) {
            return;
        }

        savingRef.current = true;
        setSaveStatus('saving');

        try {
            const seatNumberAttributeName =
                mode === 'jury-box' ? 'seatNumber' : 'alternateSeatNumber';
            const db = getDatabase();
            const fresh =
                isEdit && db ? await db.jurors.get(effectiveJurorId) : null;
            const baseRevision = fresh?.revision ?? revision;
            const editor = readEditorSnapshot();
            const useFullSnapshot = !isEdit || fresh?._localOnly;
            let sentCommentsText = null;

            let updates;
            if (useFullSnapshot) {
                updates = {
                    revision: baseRevision,
                    nameAndId,
                    demographics,
                    comments: editor.comments,
                    commentsStyles: editor.commentsStyles,
                    [seatNumberAttributeName]: seatNumber,
                };
                sentCommentsText = editor.comments;
            } else {
                updates = { revision: baseRevision };
                if (dirtyFieldsRef.current.nameAndId) {
                    updates.nameAndId = nameAndId;
                }
                if (dirtyFieldsRef.current.demographics) {
                    updates.demographics = demographics;
                }
                if (
                    dirtyFieldsRef.current.comments ||
                    editor.plainText.length > 0
                ) {
                    updates.comments = editor.comments;
                    updates.commentsStyles = editor.commentsStyles;
                    sentCommentsText = editor.comments;
                }
            }

            if (isEdit) {
                // Not in the global undo history — the dialog has its own
                // editor-level undo, and auto-saves would flood the stack.
                await updateJuror(caseId, effectiveJurorId, updates, { undoable: false });
                const updated =
                    db ? await db.jurors.get(effectiveJurorId) : null;
                if (updated?.revision != null) {
                    setRevision(updated.revision);
                }
                if (sentCommentsText != null) {
                    // Advance the fold base to what we just saved, so the
                    // live-query echo of our own write compares equal and is
                    // not folded back in as a "remote" edit. Without this,
                    // continuous typing folds stale snapshots of the line
                    // being typed back into the editor as duplicate lines.
                    lastServerCommentsRef.current = {
                        revision: updated?.revision ?? baseRevision,
                        comments: sentCommentsText,
                        styles: editor.commentsStyles,
                    };
                }
            } else {
                const created = await createJuror(caseId, {
                    ...updates,
                    created: (new Date()).toISOString(),
                });

                if (created?._id) {
                    createdJurorIdRef.current = created._id;
                    dispatch(ActionCreators.updateEditingJuror([
                        { name: '_id', value: created._id },
                    ]));
                    announceEditing(created._id);
                    lastServerCommentsRef.current = {
                        revision: created.revision || 0,
                        comments: created.comments || '',
                        styles: created.commentsStyles || [],
                    };
                }
            }

            const latestText = editorRef.current?.getPlainText?.();
            const stillDirtyComments =
                sentCommentsText != null &&
                latestText != null &&
                latestText !== sentCommentsText;

            const nextDirty = stillDirtyComments ? { comments: true } : {};
            dirtyFieldsRef.current = nextDirty;
            setDirtyFields(nextDirty);

            if (stillDirtyComments) {
                console.log(
                    'Edits arrived during save, rescheduling auto-save:',
                    effectiveJurorId,
                );
                clearTimeout(autoSaveTimerRef.current);
                autoSaveTimerRef.current = setTimeout(() => {
                    persistChangesRef.current?.();
                }, AUTO_SAVE_MS);
                setSaveStatus('pending');
            } else {
                lastPersistedRef.current = {
                    nameAndId: updates.nameAndId ?? nameAndId,
                    comments: sentCommentsText ?? editor.comments,
                    demographics: updates.demographics ?? demographics,
                };
                if (sentCommentsText != null) {
                    localCommentsTextRef.current = sentCommentsText;
                }
                setSaveStatus('saved');
                setTimeout(() => setSaveStatus('idle'), 2000);
            }
        } catch (error) {
            console.error('Auto-save failed for juror:', jurorInfo._id, error);
            setSaveStatus('error');
        } finally {
            savingRef.current = false;
        }
    }, [
        announceEditing,
        caseId,
        demographics,
        dispatch,
        hasUnpersistedChanges,
        jurorInfo,
        mode,
        nameAndId,
        previewingJuror,
        readEditorSnapshot,
        revision,
        seatNumber,
    ]);

    React.useEffect(() => {
        persistChangesRef.current = persistChanges;
    }, [persistChanges]);

    const handleClose = React.useCallback(async (event, reason) => {
        if (reason && reason === "backdropClick") {
            return;
        }

        clearTimeout(autoSaveTimerRef.current);

        if (!previewingJuror) {
            // Wait out any in-flight save so the trailing edits below see
            // the final dirty state, then flush whatever is left.
            for (let i = 0; i < 100 && savingRef.current; i++) {
                await new Promise((resolve) => setTimeout(resolve, 50));
            }
            if (
                Object.keys(dirtyFieldsRef.current).length > 0 ||
                hasUnpersistedChanges()
            ) {
                await persistChangesRef.current?.();
            }
        }

        console.log("Closing for reason: ", reason);
        dispatch(ActionCreators.closeJurorModal(caseId, jurorInfo?._id));
    }, [caseId, dispatch, hasUnpersistedChanges, jurorInfo?._id, previewingJuror]);

    const togglePreviewing = () => {
        if (previewingJuror) {
            dispatch(ActionCreators.previewJurorEnd());
        } else {
            dispatch(ActionCreators.previewJurorStart());
        }
    };

    const scheduleAutoSave = React.useCallback(() => {
        if (previewingJuror) {
            return;
        }

        setSaveStatus((prev) => (prev === 'saving' ? prev : 'pending'));
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = setTimeout(() => {
            persistChangesRef.current?.();
        }, AUTO_SAVE_MS);
    }, [previewingJuror]);

    const handleUndo = React.useCallback(() => {
        const previous = undoStackRef.current.pop();
        if (!previous) {
            setCanUndo(false);
            return;
        }

        setCanUndo(undoStackRef.current.length > 0);
        setNameAndId(previous.nameAndId);
        setDemographics([...previous.demographics]);
        localCommentsTextRef.current = previous.comments.text;

        if (editorRef.current?.restoreContent) {
            editorRef.current.restoreContent(
                previous.comments.text,
                previous.comments.styles,
            );
        } else {
            setComments({
                text: previous.comments.text,
                styles: [...previous.comments.styles],
            });
        }

        markDirty('nameAndId');
        markDirty('demographics');
        markDirty('comments');
        scheduleAutoSave();
    }, [markDirty, scheduleAutoSave]);

    const handleKeyPress = (event) => {
        if (previewingJuror) {
            return;
        }

        if ((event.metaKey || event.ctrlKey) && event.key === 'z' && !event.shiftKey) {
            event.preventDefault();
            handleUndo();
        }
    };

    const updateEditingJuror = () => {
        let changes = [
            {name: 'nameAndId', value: nameAndId},
        ];

        dispatch(ActionCreators.updateEditingJuror(changes));
    };

    const onIdChange = (event) => {
        captureUndoBeforeChange();
        const next = event.target.value;
        setNameAndId(next);
        markDirty('nameAndId');
        scheduleAutoSave();
        publishLiveState(jurorId, {
            nameAndId: next,
            activeField: 'nameAndId',
            cursor: event.target.selectionStart ?? next.length,
        });
    };

    const remoteTypists = React.useMemo(() => {
        return getLiveStatesForJuror(jurorId).filter(
            (state) => state.comments != null && state.comments !== '',
        );
    }, [getLiveStatesForJuror, jurorId]);

    const onCommentsLiveChange = React.useCallback((liveState) => {
        captureUndoBeforeChange();
        markDirty('comments');
        localCommentsTextRef.current = liveState.comments;
        // Keep the comments state in sync (the editor ignores prop changes
        // while mounted, but EmojiIndex and re-mounts read from it).
        setComments((prev) => (
            prev.text === liveState.comments
                ? prev
                : { ...prev, text: liveState.comments }
        ));
        scheduleAutoSave();
        publishLiveState(jurorId, liveState);
    }, [
        captureUndoBeforeChange,
        jurorId,
        markDirty,
        publishLiveState,
        scheduleAutoSave,
    ]);

    const onDemographicsChange = React.useCallback((newDemographics) => {
        captureUndoBeforeChange();
        setDemographics(newDemographics);
        markDirty('demographics');
        scheduleAutoSave();
    }, [captureUndoBeforeChange, markDirty, scheduleAutoSave]);

    React.useEffect(() => {
        savingRef.current = false;
        if (jurorInfo) {
            if (jurorInfo.revision) {
                setRevision(jurorInfo.revision);
            }
            if (jurorInfo.nameAndId) {
                setNameAndId(jurorInfo.nameAndId);
            }
            if (jurorInfo.demographics) {
                setDemographics([...jurorInfo.demographics]);
            }
            if (jurorInfo.comments || jurorInfo.commentsStyles) {
                setComments({
                    text: jurorInfo.comments || '',
                    styles: jurorInfo.commentsStyles || [],
                });
                localCommentsTextRef.current = jurorInfo.comments || '';
            }
        } else {
            setNameAndId('');
            setRevision(0);
            setDemographics([]);
            setDemographicsOptions([]);
            setComments({
                text: '',
                styles: [],
            });
            localCommentsTextRef.current = '';
        }
    }, [jurorInfo]);

    if (jurorInfo === null) {
        return null;
    }

    return (
        <Dialog
            open={!!jurorInfo}
            onClose={handleClose}
            aria-labelledby="form-dialog-title"
            maxWidth="md"
        >
            <DialogTitle sx={{ overflow: 'visible' }}>
                <CollaboratorInitials jurorId={jurorId} />
                <Grid container spacing={2}>
                    <Grid size={12} className={classes.dialogTitleContainer}>
                        <LiveFieldOverlay jurorId={jurorId} field="nameAndId">
                            <TextField
                                value={nameAndId}
                                disabled={previewingJuror}
                                onChange={onIdChange}
                                onFocus={() => {
                                    activeFieldRef.current = 'nameAndId';
                                }}
                                onBlur={() => {
                                    activeFieldRef.current = null;
                                    updateEditingJuror();
                                }}
                                onSelect={(e) => {
                                    publishLiveState(jurorId, {
                                        nameAndId,
                                        activeField: 'nameAndId',
                                        cursor: e.target.selectionStart ?? nameAndId.length,
                                    });
                                }}
                                autoFocus={!jurorInfo.comments}
                                id="id"
                                label="Juror ID / Name"
                                variant="outlined"
                                style={{width: '30%'}}
                                size="small"
                                className={classes.formControl}
                            />
                        </LiveFieldOverlay>
                        {jurorInfo._id &&
                            <Tooltip title={previewingJuror ? 'Allow edits' : 'View only'}
                                     className={classes.previewButton}>
                                <IconButton onClick={togglePreviewing} size="large">
                                    {previewingJuror ? <VisibilityOff key={'preview-btn'}/> :
                                        <Visibility key={'preview-btn'}/>}
                                </IconButton>
                            </Tooltip>
                        }
                        {seatNumber !== null && seatNumber !== undefined &&
                            <span className={classes.seatNumber}>
                Seat number: {seatNumber}
              </span>
                        }
                    </Grid>
                </Grid>
            </DialogTitle>
            <DialogContent onKeyDown={handleKeyPress} className={classes.dialog}>
                <Grid container spacing={2}>
                    {(caseInfo && caseInfo.demographicCategories && caseInfo.demographicCategories.length > 0) &&
                        <Grid size={12}>
                            <DemographicsOptions caseId={caseId}
                                                 disabled={previewingJuror}
                                                 options={demographicsOptions}
                                                 demographics={demographics}
                                                 setOptions={setDemographicsOptions}
                                                 setDemographics={onDemographicsChange}/>
                        </Grid>
                    }
                    <Grid size={12}>
                        {previewingJuror &&
                            <div className={rteClasses.displayContainer}>
                                <div className={rteClasses.header}>
                                    <div className={rteClasses.titleContainer}>
                                        <Typography variant={'caption'}
                                                    className={classes.commentsLabel}>Comments</Typography>
                                    </div>
                                </div>
                                <div className={rteClasses.displayEditor}>
                                    <Display text={comments.text}
                                             richTextStyles={comments.styles}
                                             key={'juror-comments'}
                                             focusOn={selectedEmoji?.emoji}
                                             focusAfter={selectedEmoji?.occurrence}/>
                                </div>
                            </div>
                        }
                        {!previewingJuror &&
                            <RichTextEditor
                                key={'juror-comments'}
                                label={<Typography variant={'caption'}
                                                   className={classes.commentsLabel}>Comments</Typography>}
                                richTextStyles={comments.styles}
                                text={comments.text}
                                remoteTypists={remoteTypists}
                                onLiveChange={onCommentsLiveChange}
                                onFocusField={() => {
                                    activeFieldRef.current = 'comments';
                                }}
                                onBlurField={() => {
                                    activeFieldRef.current = null;
                                }}
                                autoFocus
                                focusOn={selectedEmoji?.emoji}
                                focusAfter={selectedEmoji?.occurrence}
                                ref={editorRef}/>
                        }
                    </Grid>
                </Grid>
                <EmojiIndex text={localCommentsTextRef.current || comments.text || ""}
                            className={classes.emojiIndex}
                            onClick={(emoji, occurrence) => {
                                setSelectedEmoji({emoji, occurrence});
                            }}/>
            </DialogContent>
            <DialogActions className={formClasses.actions}>
                {previewingJuror &&
                    <React.Fragment>
                        <Button onClick={handleClosePreview} color="primary" variant="contained">Close</Button>
                    </React.Fragment>
                }
                {!previewingJuror &&
                    <React.Fragment>
                        <div className={classes.submitInstructions}>
                            {saveStatus === 'pending' && (
                                <Typography variant="caption" color="text.secondary">
                                    Unsaved changes…
                                </Typography>
                            )}
                            {saveStatus === 'saving' && (
                                <Typography variant="caption" color="text.secondary">
                                    Saving…
                                </Typography>
                            )}
                            {saveStatus === 'saved' && (
                                <Typography variant="caption" color="success.main">
                                    All changes saved
                                </Typography>
                            )}
                            {saveStatus === 'error' && (
                                <Typography variant="caption" color="error">
                                    Save failed — retrying on next edit
                                </Typography>
                            )}
                            {isWindows && saveStatus === 'idle' && (
                                <div>Emoji keyboard: <code className={classes.code}>Win</code> + <code className={classes.code}>.</code></div>
                            )}
                            {isMacOs && saveStatus === 'idle' && (
                                <div>Emoji keyboard: <code className={classes.code}>Cmd</code> + <code className={classes.code}>Ctrl</code> + <code className={classes.code}>Space</code></div>
                            )}
                        </div>
                        <Tooltip title="Undo (Ctrl+Z)">
                            <span>
                                <IconButton
                                    onClick={handleUndo}
                                    disabled={!canUndo}
                                    size="large"
                                    aria-label="Undo"
                                >
                                    <Undo />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Button
                            onClick={() => handleClose(null, 'closeClick')}
                            color="primary"
                            variant="contained"
                        >
                            Close
                        </Button>
                    </React.Fragment>
                }
            </DialogActions>
        </Dialog>
    );
};

export default JurorDialog;
